"use client";

/**
 * Per-character / per-line text reveal (Venetian recipe 2).
 *
 * chars/words: from { y: 30, opacity: 0, duration: 0.3, stagger: 0.05,
 * ease: power2.out }. lines: { y: 20, opacity: 0, duration: 0.35,
 * stagger: 0.1 }. Trigger "top 80%", plays once. Splitting waits for
 * document.fonts.ready so line breaks and metrics are final.
 *
 * The Venetian source offsets paragraph lines -=0.15 after their heading;
 * as standalone components we expose that as `delay` (seconds), e.g.
 * heading duration 0.3 + chars stagger, then paragraph with delay ~0.3.
 *
 * Accessibility: content is server-rendered and fully visible without JS.
 * The hidden state is only applied by GSAP at tween creation, and the
 * whole effect is skipped under prefers-reduced-motion. SplitText's aria
 * handling keeps the original text readable to assistive tech.
 */
import {
  useRef,
  type ElementType,
  type HTMLAttributes,
  type Ref,
} from "react";
import { gsap, SplitText, useGSAP, prefersReducedMotion } from "./gsap";
import { MotionSafe } from "./MotionSafe";

type SplitType = "chars" | "words" | "lines";

export interface SplitRevealProps extends HTMLAttributes<HTMLElement> {
  /** Element to render, e.g. "h2", "p". Defaults to "div". */
  as?: ElementType;
  /** Split granularity. Defaults to "chars" (headings). Use "lines" for paragraphs. */
  type?: SplitType;
  /** Extra delay in seconds once triggered (sequencing after a heading). */
  delay?: number;
  /**
   * Above-the-fold opt-out: play on mount instead of waiting for a
   * ScrollTrigger. The markup is server-rendered and visible either way;
   * this only removes the scroll dependency for hero content that is
   * already in the first viewport.
   */
  immediate?: boolean;
}

const TWEEN_BY_TYPE: Record<
  SplitType,
  { y: number; duration: number; stagger: number }
> = {
  chars: { y: 30, duration: 0.3, stagger: 0.05 },
  words: { y: 30, duration: 0.3, stagger: 0.05 },
  lines: { y: 20, duration: 0.35, stagger: 0.1 },
};

function SplitRevealAnimated({
  as = "div",
  type = "chars",
  delay = 0,
  immediate = false,
  children,
  ...rest
}: SplitRevealProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    (_, contextSafe) => {
      const el = ref.current;
      if (!el || prefersReducedMotion() || !contextSafe) return;

      let split: SplitText | null = null;
      // document.fonts.ready cannot be cancelled, so the continuation below
      // can resolve after this effect has already been cleaned up. Splitting
      // at that point rewrites children of a node React is in the middle of
      // reconciling, which throws NotFoundError on the subsequent removal.
      // `cancelled` closes that window (MotionProvider guards its own
      // fonts.ready refresh the same way). el.isConnected is not sufficient:
      // effect cleanups run before React detaches the DOM node, so the
      // element still reports connected during the exact race we care about.
      let cancelled = false;
      const run = contextSafe(() => {
        if (cancelled) return;
        // Component may have unmounted while fonts were loading.
        if (!el.isConnected) return;
        // Chars must be wrapped in word units too: bare char spans are
        // individual inline-blocks, so the browser happily wraps a line
        // mid-word ("layers" breaking as "lay/ers"). Splitting words as
        // well keeps each word atomic at line breaks.
        split = new SplitText(el, {
          type: type === "chars" ? "chars,words" : type,
        });
        const targets =
          type === "lines"
            ? split.lines
            : type === "words"
              ? split.words
              : split.chars;
        if (!targets.length) return;
        const { y, duration, stagger } = TWEEN_BY_TYPE[type];
        gsap.from(targets, {
          y,
          opacity: 0,
          duration,
          stagger,
          delay,
          ease: "power2.out",
          ...(immediate
            ? {}
            : {
                scrollTrigger: {
                  trigger: el,
                  start: "top 80%",
                  once: true,
                },
              }),
          // Once played (it only plays once), restore the original DOM:
          // native kerning and ligatures come back and no split artefacts
          // linger for the rest of the session.
          onComplete: () => {
            split?.revert();
            split = null;
          },
        });
      });

      // Split only after webfonts settle; Fraunces swap reflows every line.
      document.fonts.ready.then(run);

      return () => {
        cancelled = true;
        split?.revert();
      };
    },
    { scope: ref }
  );

  const Tag = as;
  return (
    <Tag ref={ref as Ref<HTMLElement>} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * SplitText rewrites React-owned DOM, so a throw here would otherwise
 * unmount the entire page. MotionSafe degrades this one heading to the
 * identical static markup instead: same tag, same classes, same text, no
 * animation. Content is server-rendered and visible either way, so the
 * fallback is visually indistinguishable once the reveal would have
 * finished.
 */
export function SplitReveal(props: SplitRevealProps) {
  const { as = "div", type, delay, immediate, children, ...domProps } = props;
  const Tag = as;
  // `type`, `delay` and `immediate` are animation-only and must not reach
  // the DOM element in the fallback; they are consumed here and dropped.
  void type;
  void delay;
  void immediate;

  return (
    <MotionSafe fallback={<Tag {...domProps}>{children}</Tag>}>
      <SplitRevealAnimated {...props} />
    </MotionSafe>
  );
}
